import React, { createContext, useCallback, useContext, useLayoutEffect, useState, ReactNode } from 'react';
import { colors, componentColors, themeConfig } from '../styles/colors';
import { STORAGE_KEYS } from '../constants/storage';

export type ThemeMode = 'light' | 'night';

interface ThemeContextType {
  colors: typeof colors;
  componentColors: typeof componentColors;
  themeConfig: typeof themeConfig;
  mode: ThemeMode;
  isNightMode: boolean;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const applyThemeClass = (mode: ThemeMode) => {
  const isNight = mode === 'night';
  document.documentElement.classList.toggle('night-mode', isNight);
  document.body.classList.toggle('night-mode', isNight);
  document.documentElement.style.colorScheme = isNight ? 'dark' : 'light';
};

const readStoredTheme = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.THEME_MODE);
    if (stored === 'night' || stored === 'light') {
      return stored;
    }
  } catch {
    // ignore storage errors
  }
  return 'light';
};

const persistTheme = (mode: ThemeMode) => {
  try {
    localStorage.setItem(STORAGE_KEYS.THEME_MODE, mode);
  } catch {
    // ignore storage errors
  }
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('night-mode')
      ? 'night'
      : 'light',
  );

  useLayoutEffect(() => {
    const stored = readStoredTheme();
    setMode(stored);
    applyThemeClass(stored);
  }, []);

  const setTheme = useCallback((next: ThemeMode) => {
    setMode(next);
    persistTheme(next);
    applyThemeClass(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(mode === 'night' ? 'light' : 'night');
  }, [mode, setTheme]);

  const value: ThemeContextType = {
    colors,
    componentColors,
    themeConfig,
    mode,
    isNightMode: mode === 'night',
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
